/**
 * Navigation instrumentation — events for measurability.
 *
 * Events:
 * - nav_click(itemId, fromRoute) — user clicks a nav item
 * - route_view(routeId, shell) — route entry
 * - route_ready(routeId, duration_ms?) — destination ready (see useRouteReady)
 * - access_denied(routeId) — 403 or guard redirect
 *
 * Implementation: client-side emit via console + custom event.
 * Production: replace emitNavEvent with your analytics pipeline.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getNavItemByPath } from '@/config/nav';
import { getShellFromPath } from '@/config/routeMetadata';

type NavEventType = 'nav_click' | 'route_view' | 'route_ready' | 'access_denied';

interface NavEventPayload {
  route_id?: string;
  item_id?: string;
  from_route?: string;
  shell?: string;
  duration_ms?: number;
}

/** Emit a navigation analytics event. Replace with your analytics pipeline in production. */
export function emitNavEvent(type: NavEventType, payload: NavEventPayload) {
  if (import.meta.env.DEV) {
    console.debug(`[nav:${type}]`, payload);
  }
  // Dispatch custom event for analytics listeners
  window.dispatchEvent(
    new CustomEvent('amuster:nav', { detail: { type, ...payload, timestamp: Date.now() } }),
  );
}

/** Fire a nav_click event and set a performance mark for time-to-destination */
export function trackNavClick(itemId: string, targetRouteId: string, fromRoute: string) {
  emitNavEvent('nav_click', { item_id: itemId, route_id: targetRouteId, from_route: fromRoute });
  // Set performance mark for time-to-destination measurement
  const markName = `nav_click:${targetRouteId}`;
  try {
    performance.clearMarks(markName); // Clear any stale mark
    performance.mark(markName);
  } catch { /* Performance API not available */ }
}

/** Fire an access_denied event */
export function trackAccessDenied(routeId: string) {
  emitNavEvent('access_denied', { route_id: routeId });
}

/**
 * useRouteView — fires route_view on every route change.
 * Mount once in the layout component.
 */
export function useRouteView() {
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    const path = location.pathname;
    if (path === prevPath.current) return;
    prevPath.current = path;

    const item = getNavItemByPath(path);
    const routeId = item?.route_id ?? path;
    const shell = getShellFromPath(path);

    emitNavEvent('route_view', { route_id: routeId, shell });
  }, [location.pathname]);
}
